import { Directive, ElementRef, HostListener, Renderer2, Input, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';

// Define the type of the patterns object
interface Patterns {
    [key: string]: string;  // Allows any string key with a string value
}

@Directive({
    selector: '[input-mask]',
})
export class InputMaskDirective {
    @Input()
    enableValidation = true;

    constructor(
        public elementRef: ElementRef,
        private renderer2: Renderer2,
        @Optional() @Self() private ngControl: NgControl
    ) { }

    @Input('input-mask')
    set setType(type: string) {
        if (this.patterns[type]) {
            this.pattern = new RegExp(this.patterns[type]);
        }
    }

    pattern: { test: any } = {
        test: (value: any) => false
    };

    // Define patterns object with a more specific type
    patterns: Patterns = {
        numberOnly: '^[0-9]*$', // Allows empty input
        mobileNumberOnly: '^[0-9]*$',
        numberWithoutOnly: '^[0-9]*$',
        numberandCommaOnly: '^[0-9 _ ,]*$',
        numberandDotAndCommaOnly: '^[0-9 _ ,.]*$',
        numberandDotOnly: '^[0-9]*(\\.[0-9]{0,4})?$',
        numberandDotOnlyLimit2Digits: '^[0-9]*(\\.[0-9]{0,2})?$',
        numberandDotOnlyAndNotStartWithZero: '^(?!0\\d*)+[1-9]+(\\d*(?:\\.\\d{0,2})?)$',
        pan: '^[a-zA-Z0-9]*$',
        letterOnly: '^[a-zA-Z]+(?:[a-zA-Z]| (?! ))*$',
        letterAndSqts: '^[a-zA-Z \']+$',
        letterAndNumbersOnly: '^[a-zA-Z0-9 ]*$',
        numberWithSpace: '^[0-9 ]+$',
        email: '^[a-zA-Z0-9.@_-][a-zA-Z0-9.@_-]*$',
        numberOnlyDontStartWithZero: '^(?![0.]+$)(?:0|[0-9 _ ,]+$)?$',
        numberOnlyDontStartWithZeroTds: '^(?![0.]+$)(?:0|[0-9 _ ,]+$)?$',
        numberOnlyStartWithFive: '^(?![0,1,2,3,4.]+$)(?:0|[0-9]+$)?$',
        CurrencyDontStartWithZero: '^(?![0,.]+$)(?:0|[0-9,.]+$)?$',
        drivingLicence: '^[^ ][a-zA-Z0-9 -]*$',
        aadhar: '^[0-9][0-9 ]*$',
        noSpaceAadhar: '^[0-9][0-9]*$',
        address: '^[a-zA-Z0-9/:;()&.,\'_#|•. -]*$',
        ifscSearch: '^[a-zA-Z0-9/:;()&.,\'-_ #|•.]*$',
        pincodePattern: '^(?![0.]+$)(?:0|[0-9]+$)?$',
        agentCode: '^[A-Za-z0-9- ]*$',
        cc: '^(?![.]+$)(?:0|[0-9 .]+$)?$',
        numberandDotOnlyLimitDigits: '^[0-9]{0,9}(\\.[0-9]{0,4})?$',
        numberandDotOnly13LimitDigits: '^[0-9]{0,13}(\\.[0-9]{0,4})?$',
        numberAndText: '^[a-zA-Z0-9]*$',
    };

    _preValue: any = '';

    @HostListener('input', ['$event'])
    public onInput(e: any): void {
        this.applyMask(e.target.value);
    }

    @HostListener('change', ['$event'])
    public onInputChange(e: any): void {
        this.applyMask(e.target.value);
    }

    public validateValue(value: string | number): boolean {
        return this.pattern.test(String(value));
    }

    private applyMask(value: string | number): void {
        if (this.enableValidation) {
            if (value === '' || value === null || value === undefined) {
                this._preValue = '';
                value = '';
            } else if (this.validateValue(value)) {
                this._preValue = value;
            } else {
                value = this._preValue;
            }
        }
        this.renderer2.setProperty(this.elementRef.nativeElement, 'value', value);
        if (this.ngControl?.control) {
            this.ngControl.control.setValue(value, { emitEvent: false });
        }
    }

    /** Called by Angular when the form control value changes programmatically */
    public writeValue(value: string | number): void {
        this.renderer2.setProperty(this.elementRef.nativeElement, 'value', value ?? '');
    }
}